"use client";

import { useAuth } from "@/lib/auth-context";
import { useEffect, useState, useCallback, useRef } from "react";
import { PageLoader } from "../components/page-loader";
import { useToast } from "../components/toast";
import { useConfirm } from "../components/confirm";

interface Member {
  membershipId: string;
  userId: string;
  email: string;
  displayName: string;
  role: string;
  createdDate: string;
}

interface Invitation {
  invitationId: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  invitedByName: string;
  createdDate: string;
}

const roles = ["viewer", "member", "admin"] as const;

function MobileMemberCard({ m, isOwnerOrAdmin, onChangeRole, onRemove }: {
  m: Member;
  isOwnerOrAdmin: boolean;
  onChangeRole: (id: string, role: string) => void;
  onRemove: (id: string) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const canManage = isOwnerOrAdmin && m.role !== "owner";

  return (
    <div className="border-b border-white/5 py-4 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/5 text-sm font-bold text-base-content/40">
          {(m.displayName || m.email).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{m.displayName || "\u2014"}</p>
          <p className="truncate text-xs text-base-content/30">{m.email}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.role === "owner" ? "border border-primary/20 bg-primary/5 text-primary" : "border border-white/5 text-base-content/40"}`}>
          {m.role}
        </span>
        {canManage && (
          <div ref={ref} className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-base-content/30 transition hover:bg-white/5"
              aria-label="Member actions"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
              </svg>
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full z-50 mt-1 w-48 overflow-hidden rounded-xl border border-white/5 bg-base-200 shadow-xl">
                <div className="border-b border-white/5 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-base-content/20">Change role</p>
                </div>
                {roles.map((role) => (
                  <button
                    key={role}
                    onClick={() => { onChangeRole(m.membershipId, role); setMenuOpen(false); }}
                    className={`flex w-full items-center justify-between px-4 py-3 text-sm transition hover:bg-white/5 ${
                      m.role === role ? "text-primary font-medium" : "text-base-content/60"
                    }`}
                  >
                    <span className="capitalize">{role}</span>
                    {m.role === role && (
                      <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                ))}
                <div className="border-t border-white/5">
                  <button
                    onClick={() => { onRemove(m.membershipId); setMenuOpen(false); }}
                    className="flex w-full items-center gap-2 px-4 py-3 text-sm text-error/60 transition hover:bg-error/5 hover:text-error"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
                    </svg>
                    Remove member
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MembersPage() {
  const { tenant, fetchWithAuth } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ signInLink: string | null; inviteUrl: string; email: string } | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { confirm } = useConfirm();

  const isOwnerOrAdmin = tenant?.role === "owner" || tenant?.role === "admin";
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail);

  const load = useCallback(async () => {
    const [mRes, iRes] = await Promise.all([
      fetchWithAuth("/platform/members"),
      isOwnerOrAdmin ? fetchWithAuth("/platform/invitations") : null,
    ]);
    if (mRes.ok) setMembers(await mRes.json());
    if (iRes?.ok) setInvitations(await iRes.json());
    setLoading(false);
  }, [fetchWithAuth, isOwnerOrAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    setInviting(true);
    setError("");
    setInviteResult(null);
    const res = await fetchWithAuth("/platform/invitations", {
      method: "POST",
      body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
    });
    const data = await res.json();
    if (res.ok) {
      setInviteResult({ signInLink: data.signInLink, inviteUrl: data.inviteUrl, email: inviteEmail });
      setInviteEmail("");
      load();
    } else {
      setError(data.error);
    }
    setInviting(false);
  };

  const handleChangeRole = async (membershipId: string, role: string) => {
    try {
      const res = await fetchWithAuth(`/platform/members/${membershipId}/role`, {
        method: "PUT",
        body: JSON.stringify({ role }),
      });
      if (res.ok) {
        toast(`Role updated to ${role}`);
      } else {
        const data = await res.json().catch(() => ({}));
        toast(data.error || "Failed to update role", "error");
      }
    } catch {
      toast("Failed to update role", "error");
    }
    load();
  };

  const handleRemove = async (membershipId: string) => {
    const ok = await confirm({
      title: "Remove member",
      message: "They will lose access to this organization immediately.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetchWithAuth(`/platform/members/${membershipId}`, { method: "DELETE" });
      if (res.ok) {
        toast("Member removed");
      } else {
        toast("Failed to remove member", "error");
      }
    } catch {
      toast("Failed to remove member", "error");
    }
    load();
  };

  const handleRevokeInvite = async (id: string) => {
    try {
      const res = await fetchWithAuth(`/platform/invitations/${id}`, { method: "DELETE" });
      if (res.ok) {
        toast("Invitation revoked");
      } else {
        toast("Failed to revoke invitation", "error");
      }
    } catch {
      toast("Failed to revoke invitation", "error");
    }
    load();
  };

  if (!tenant || loading) return <PageLoader />;

  const pendingInvitations = invitations.filter(i => i.status === "pending");
  const shareLink = inviteResult?.signInLink || inviteResult?.inviteUrl || "";

  return (
    <div className="space-y-6">
      {/* Invite */}
      {isOwnerOrAdmin && (
        <div className="rounded-xl border border-white/5 bg-base-100 p-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Invite Member</p>
          <p className="mt-1 text-sm text-base-content/30">Send an invitation link to add someone to {tenant.name}.</p>

          {error && (
            <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-error/20 bg-error/5 px-4 py-3 text-sm text-error">
              <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          {inviteResult && (
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-success/20 bg-success/5 p-4">
              <svg className="mt-0.5 h-5 w-5 shrink-0 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm">Invitation sent to <span className="font-semibold">{inviteResult.email}</span>. Share this link:</p>
                <code className="mt-2 block select-all break-all rounded-lg bg-black/20 p-3 font-mono text-xs">{shareLink}</code>
              </div>
              <button className="rounded-md p-1 text-base-content/30 transition hover:text-base-content" onClick={() => setInviteResult(null)}>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          )}

          <div className="mt-4 space-y-2.5 sm:space-y-0 sm:flex sm:items-start sm:gap-3">
            <div className="min-w-0 sm:flex-1">
              <input
                type="email"
                className="input input-bordered w-full bg-white/[0.02] validator"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && isValidEmail && handleInvite()}
                required
              />
              <p className="validator-hint text-xs text-error mt-1">Please enter a valid email address</p>
            </div>
            <select
              className="select select-bordered w-full bg-base-200 sm:w-auto"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="viewer">Viewer</option>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <button
              className={`btn btn-primary w-full rounded-lg sm:w-auto sm:px-6 ${inviting ? "btn-disabled" : ""}`}
              onClick={handleInvite}
              disabled={!isValidEmail}
            >
              {inviting ? <span className="loading loading-spinner loading-sm"></span> : "Send Invite"}
            </button>
          </div>
        </div>
      )}

      {/* Members */}
      <div className="rounded-xl border border-white/5 bg-base-100 p-6">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Team</p>
          <span className="text-xs text-base-content/20">{members.length} member{members.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="mt-4 divide-y divide-white/5">
          {/* Desktop */}
          <div className="hidden sm:block">
            {members.map((m) => (
              <div key={m.membershipId} className="flex items-center gap-4 border-b border-white/5 py-3.5 last:border-b-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5 text-sm font-bold text-base-content/40">
                  {(m.displayName || m.email).charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{m.displayName || "—"}</p>
                  <p className="truncate text-xs text-base-content/30">{m.email}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {isOwnerOrAdmin && m.role !== "owner" ? (
                    <select
                      className="select select-bordered select-xs bg-base-200"
                      value={m.role}
                      onChange={(e) => handleChangeRole(m.membershipId, e.target.value)}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                  ) : (
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.role === "owner" ? "border border-primary/20 bg-primary/5 text-primary" : "border border-white/5 text-base-content/40"}`}>
                      {m.role}
                    </span>
                  )}
                  <span className="text-xs text-base-content/20">
                    {new Date(m.createdDate).toLocaleDateString()}
                  </span>
                  {isOwnerOrAdmin && m.role !== "owner" && (
                    <button
                      className="rounded-md p-1.5 text-base-content/20 transition hover:bg-error/10 hover:text-error"
                      onClick={() => handleRemove(m.membershipId)}
                      title="Remove member"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile */}
          <div className="sm:hidden">
            {members.map((m) => (
              <MobileMemberCard
                key={m.membershipId}
                m={m}
                isOwnerOrAdmin={isOwnerOrAdmin}
                onChangeRole={handleChangeRole}
                onRemove={handleRemove}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Pending invitations */}
      {isOwnerOrAdmin && pendingInvitations.length > 0 && (
        <div className="rounded-xl border border-white/5 bg-base-100 p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-widest text-base-content/30">Pending Invitations</p>
            <span className="rounded-full border border-warning/20 bg-warning/5 px-2 py-0.5 text-[10px] font-semibold text-warning">
              {pendingInvitations.length}
            </span>
          </div>

          <div className="mt-4 divide-y divide-white/5">
            {/* Desktop */}
            <div className="hidden sm:block">
              {pendingInvitations.map((i) => (
                <div key={i.invitationId} className="flex items-center gap-4 border-b border-white/5 py-3.5 last:border-b-0">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-warning/5 text-sm">
                    <svg className="h-4 w-4 text-warning/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{i.email}</p>
                    <p className="text-xs text-base-content/30">
                      Invited by {i.invitedByName} · Expires {new Date(i.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="rounded-full border border-white/5 px-2 py-0.5 text-[10px] font-semibold text-base-content/40">{i.role}</span>
                    <button
                      className="rounded-md p-1.5 text-base-content/20 transition hover:bg-error/10 hover:text-error"
                      onClick={() => handleRevokeInvite(i.invitationId)}
                      title="Revoke invitation"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile */}
            <div className="sm:hidden">
              {pendingInvitations.map((i) => (
                <div key={i.invitationId} className="space-y-3 border-b border-white/5 py-4 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/5 text-sm">
                      <svg className="h-4 w-4 text-warning/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{i.email}</p>
                      <p className="text-xs text-base-content/30">
                        {i.role} · by {i.invitedByName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pl-[52px]">
                    <span className="text-xs text-base-content/30">
                      Expires {new Date(i.expiresAt).toLocaleDateString()}
                    </span>
                    <button
                      className="rounded-lg border border-error/10 px-4 py-2 text-xs text-base-content/30 transition hover:bg-error/10 hover:text-error"
                      onClick={() => handleRevokeInvite(i.invitationId)}
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
